import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Valid roles in the system
export type UserRole = 'student' | 'parent' | 'mentor' | 'counselor';

interface User {
  id: string;
  name: string;
  email: string;
  profilePicture?: string;
  authProvider: string;
  hasProject?: boolean;
  userType?: string; // Comma-separated roles for backwards compatibility
  roles?: UserRole[]; // Array of roles
  isAdmin?: boolean;
  trialEndDate?: string | Date;
  subscriptionStatus?: string;
  subscriptionEndDate?: string | Date;
}

interface UserContextType {
  user: User | null;
  token: string | null;
  login: (token: string, userData: User) => void;
  logout: () => void;
  isLoading: boolean;
  updateUserProject: (hasProject: boolean) => void;
  updateUserType: (userType: UserRole) => void;
  // New multi-role support
  roles: UserRole[];
  activeRole: UserRole;
  setActiveRole: (role: UserRole) => void;
  hasRole: (role: UserRole) => boolean;
  addRole: (role: UserRole) => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const clearStoredAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('activeRole');
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

// Helper to parse roles from userType string or roles array
const parseRoles = (user: User | null): UserRole[] => {
  if (!user) return ['student'];

  // If roles array is provided, use it
  if (user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
    return user.roles;
  }

  // Otherwise parse from userType string
  if (user.userType) {
    const parsed = user.userType.split(',').map(r => r.trim()).filter(r => r) as UserRole[];
    return parsed.length > 0 ? parsed : ['student'];
  }

  return ['student'];
};

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeRole, setActiveRoleState] = useState<UserRole>('student');

  // Compute roles from user
  const roles = parseRoles(user);

  useEffect(() => {
    // Check for existing auth on app startup
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedActiveRole = localStorage.getItem('activeRole') as UserRole | null;

    console.log('DEBUG - UserContext initialization:', {
      hasToken: !!storedToken,
      hasUser: !!storedUser,
      tokenPreview: storedToken ? `${storedToken.substring(0, 20)}...` : 'none'
    });

    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        console.log('DEBUG - User data loaded:', { id: userData.id, email: userData.email, name: userData.name, roles: userData.roles });
        setToken(storedToken);
        setUser(userData);

        // Restore active role, default to first role if not set
        const userRoles = parseRoles(userData);
        if (storedActiveRole && userRoles.includes(storedActiveRole)) {
          setActiveRoleState(storedActiveRole);
        } else {
          setActiveRoleState(userRoles[0]);
        }

        // Validate the token with the backend; clear the session if it's
        // expired or invalid so the app doesn't stay stuck "logged in".
        fetch(`${API_BASE}/auth/profile`, {
          headers: { Authorization: `Bearer ${storedToken}` },
        })
          .then(async (res) => {
            if (res.status === 401 || res.status === 403) {
              console.warn('Stored session is no longer valid, signing out');
              clearStoredAuth();
              setToken(null);
              setUser(null);
              setActiveRoleState('student');
              return;
            }
            if (res.ok) {
              // Refresh server-derived flags (e.g. isAdmin) on stored sessions
              try {
                const data = await res.json();
                if (data?.user && typeof data.user.isAdmin === 'boolean') {
                  setUser((prev) => {
                    if (!prev || prev.isAdmin === data.user.isAdmin) return prev;
                    const updated = { ...prev, isAdmin: data.user.isAdmin };
                    localStorage.setItem('user', JSON.stringify(updated));
                    return updated;
                  });
                }
              } catch {
                // ignore malformed profile response
              }
            }
          })
          .catch(() => {
            // Network error (e.g. backend down): keep the session for now
          });
      } catch (error) {
        console.error('Failed to parse stored user data:', error);
        // Clear invalid data
        clearStoredAuth();
      }
    } else {
      console.log('DEBUG - No stored authentication found');
    }

    setIsLoading(false);
  }, []);

  const login = (newToken: string, userData: User) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);

    // Set initial active role to first role
    const userRoles = parseRoles(userData);
    const initialRole = userRoles[0];
    setActiveRoleState(initialRole);
    localStorage.setItem('activeRole', initialRole);
  };

  const logout = () => {
    clearStoredAuth();
    setToken(null);
    setUser(null);
    setActiveRoleState('student');
  };

  const updateUserProject = (hasProject: boolean) => {
    if (user) {
      const updatedUser = { ...user, hasProject };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const updateUserType = (userType: UserRole) => {
    if (user) {
      const updatedUser = { ...user, userType, roles: [userType] };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const setActiveRole = (role: UserRole) => {
    if (roles.includes(role)) {
      setActiveRoleState(role);
      localStorage.setItem('activeRole', role);
    }
  };

  const hasRole = (role: UserRole): boolean => {
    return roles.includes(role);
  };

  const addRole = async (role: UserRole): Promise<void> => {
    if (!token || !user) return;

    try {
      const response = await fetch(`${API_BASE}/auth/roles/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role })
      });

      if (response.ok) {
        const data = await response.json();
        const updatedUser = { ...user, userType: data.user.userType, roles: data.user.roles };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Failed to add role:', error);
    }
  };

  const value = {
    user,
    token,
    login,
    logout,
    isLoading,
    updateUserProject,
    updateUserType,
    roles,
    activeRole,
    setActiveRole,
    hasRole,
    addRole
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};